import { promiseQueueHelper } from "@/helpers";
import {
  breakIntoChunks,
  fetchBaseQueryWrapper,
  fileReadAsBase64Async,
} from "@/services/helpers";
import { createApi } from "@reduxjs/toolkit/dist/query/react";

export const storageApi = createApi({
  reducerPath: "storageApi",
  baseQuery: fetchBaseQueryWrapper({
    path: "storage",
  }),
  endpoints: (build) => ({
    getSas: build.mutation({
      query: ({ storageInfo, intakeId }) => ({
        url: `${intakeId}/sas`,
        method: "POST",
        body: storageInfo,
      }),
    }),
    uploadFile: build.mutation({
      async queryFn(
        { file, location, onProgress },
        _queryApi,
        _extraOptions,
        fetchWithBQ
      ) {
        const chunks = breakIntoChunks(file.size);
        const concurrentPromises = 3;
        const promises = [];
        const uploadStep = 100 / chunks.length;
        let currentProgress = 0;
        chunks.forEach((chunk) =>
          promises.push(async () => {
            const blob = file.slice(
              chunk.startPosition,
              chunk.endPosition,
              "application/octet-binary"
            );
            const base64 = await fileReadAsBase64Async(blob);
            await fetchWithBQ({
              url: `base64/putBlock`,
              method: "PUT",
              body: {
                itemName: location,
                blockId: chunk.chunkId,
                base64Data: base64,
              },
            }).then(() => {
              currentProgress += uploadStep;
              if (onProgress) {
                onProgress({
                  percent: currentProgress,
                  loaded: (currentProgress / 100) * file.size,
                  total: file.size,
                });
              }
            });
          })
        );
        await promiseQueueHelper(promises, concurrentPromises);
        return {
          data: {
            location: location,
            blockIds: chunks.map((t) => t.chunkId),
          },
        };
      },
    }),
    deleteUploads: build.mutation({
      query: ({ intakeId }) => ({
        url: `${encodeURIComponent(intakeId)}`,
        method: "DELETE",
      }),
    }),
    download: build.mutation({
      query: ({ url }) => ({
        url: `file/${url}`,
      }),
    }),
    getCDNFilePath: build.query({
      query: ({ projectId, fileId, isInitiative }) => ({
        url: isInitiative
          ? `/initiatives/${projectId}/${fileId}`
          : `/${projectId}/${fileId}`,
      }),
      transformResponse: (response) => response.data,
    }),
  }),
});
export const {
  useGetSasMutation,
  useUploadFileMutation,
  useDeleteUploadsMutation,
  useDownloadMutation,
  useGetCDNFilePathQuery,
} = storageApi;
